import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "wouter";
import { MailCheck } from "lucide-react";
import {
  forgotPasswordSchema,
  type ForgotPasswordInput,
} from "@shared/validation/auth.schema";
import { useForgotPassword } from "../lib/api/usePassword";
import { INPUT_CLASS } from "../lib/forms/inputClass";
import { AuthHero } from "../components/AuthHero";
import { BrandWordmark } from "../components/BrandWordmark";
import { Button } from "../components/ui/Button";
import { Field } from "../components/ui/Field";
import { Reveal } from "../components/ui/Reveal";

export function ForgotPassword() {
  const forgot = useForgotPassword();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordInput>({ resolver: zodResolver(forgotPasswordSchema) });

  return (
    <main className="min-h-screen lg:grid lg:grid-cols-2">
      <AuthHero />
      <div className="flex min-h-screen flex-col bg-white">
        <header className="px-6 py-5 lg:hidden">
          <Link href="/" className="inline-flex items-center gap-2">
            <BrandWordmark className="h-6" />
          </Link>
        </header>
        <div className="flex flex-1 items-center justify-center px-6 py-12">
          <Reveal className="w-full max-w-sm">
            {forgot.isSuccess ? (
              <>
                <MailCheck className="h-8 w-8 text-emerald-600" aria-hidden />
                <h1 className="mt-3 font-display text-2xl font-bold tracking-tight text-neutral-900">
                  Check your email
                </h1>
                {/* Worded so it is true whether or not an account exists — the
                    server answers identically either way, and the UI must not
                    undo that by implying the address was recognised. */}
                <p className="mt-1.5 text-sm text-neutral-600">
                  If that address has an account, a reset link is on its way. It works once
                  and expires in an hour.
                </p>
                <p className="mt-4 text-sm text-neutral-600">
                  <Link href="/login" className="font-medium text-sky-700 hover:underline">
                    Back to sign in
                  </Link>
                </p>
              </>
            ) : (
              <>
                <h1 className="font-display text-2xl font-bold tracking-tight text-neutral-900 sm:text-3xl">
                  Forgot your password?
                </h1>
                <p className="mt-1.5 text-sm text-neutral-600">
                  Enter your email and we&apos;ll send you a link to choose a new one.
                </p>
                <form
                  className="mt-6 space-y-4"
                  onSubmit={handleSubmit((v) => forgot.mutate(v))}
                  noValidate
                >
                  <Field label="Email" htmlFor="forgot-email" error={errors.email?.message}>
                    <input
                      id="forgot-email"
                      type="email"
                      autoComplete="email"
                      autoFocus
                      {...register("email")}
                      className={INPUT_CLASS}
                    />
                  </Field>
                  {forgot.isError && (
                    <p role="alert" className="text-sm font-medium text-red-700">
                      {forgot.error.code === "RATE_LIMITED"
                        ? "Too many requests — please wait a while and try again."
                        : "Something went wrong. Please try again."}
                    </p>
                  )}
                  <Button type="submit" className="w-full" isLoading={forgot.isPending}>
                    Send reset link
                  </Button>
                </form>
                <p className="mt-6 text-sm text-neutral-600">
                  Remembered it?{" "}
                  <Link href="/login" className="font-medium text-sky-700 hover:underline">
                    Sign in
                  </Link>
                </p>
              </>
            )}
          </Reveal>
        </div>
      </div>
    </main>
  );
}
